import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Mail, MapPin, Phone, Search, X } from "lucide-react";
import {
  fetchArlaAerialProducts,
  type ArlaAerialProductItem,
  type ArlaAerialProductsPayload,
} from "../../api/arlaAerialProducts";
import { cn } from "../utils/cn";

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

function matchesSearch(item: ArlaAerialProductItem, query: string): boolean {
  if (!query) return true;
  const haystack = [
    item.regnum,
    item.product_name,
    item.registrant,
    item.product_type_label,
    item.auth_snippets,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function formatUpdatedAt(timestamp: number): string {
  if (!timestamp) return "—";
  return new Intl.DateTimeFormat("fr-CA", { dateStyle: "long" }).format(new Date(timestamp * 1000));
}

export function ArlaAerialProductsPage() {
  const [payload, setPayload] = useState<ArlaAerialProductsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [registrantFilter, setRegistrantFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchArlaAerialProducts()
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPayload(null);
          setError(err instanceof Error ? err.message : "Impossible de charger les produits ARLA");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizedSearch = normalizeSearch(search);

  const filteredItems = useMemo(() => {
    if (!payload) return [];
    return payload.items.filter((item) => {
      if (typeFilter && item.product_type !== typeFilter) return false;
      if (registrantFilter && item.registrant !== registrantFilter) return false;
      return matchesSearch(item, normalizedSearch);
    });
  }, [payload, typeFilter, registrantFilter, normalizedSearch]);

  const hasActiveFilters = Boolean(normalizedSearch || typeFilter || registrantFilter);

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("");
    setRegistrantFilter("");
  };

  return (
    <div className="min-h-screen bg-[#f4f7f5] text-[#1f2937]">
      <header className="bg-gradient-to-br from-[#12372f] to-[#0f766e] text-white">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-6 px-4 py-6 md:flex-row md:items-center">
          <div className="flex items-center justify-start">
            <img
              src="/logo/xlkey-logo-blanc.svg"
              alt="XLKey"
              className="h-14 w-auto max-w-[280px] md:h-16"
            />
          </div>
          <div className="flex w-full flex-col items-start gap-3 md:ml-auto md:w-auto md:items-end">
            <a
              href="tel:+15819949171"
              className="inline-flex items-center gap-2.5 text-sm text-[#ecfdf5] transition-colors hover:text-white"
            >
              <Phone size={18} className="shrink-0" aria-hidden="true" />
              <span>
                Appelez-nous : <strong className="font-bold text-white">(581) 994-9171</strong>
              </span>
            </a>
            <a
              href="mailto:info@xlkey.ca"
              className="inline-flex items-center gap-2.5 text-sm text-[#ecfdf5] transition-colors hover:text-white"
            >
              <Mail size={18} className="shrink-0" aria-hidden="true" />
              <span>
                Écrivez-nous : <strong className="font-bold text-white">info@xlkey.ca</strong>
              </span>
            </a>
            <p className="inline-flex items-center gap-2.5 text-sm text-[#ecfdf5]">
              <MapPin size={18} className="shrink-0" aria-hidden="true" />
              <span>Partout au Québec</span>
            </p>
          </div>
        </div>
        <div className="border-t border-white/15 px-4 py-8 text-center">
          <h1 className="mx-auto mb-4 max-w-4xl text-[clamp(1.5rem,4.5vw,2.375rem)] font-extrabold uppercase leading-tight tracking-wide">
            Produits homologués
            <br />
            application aérienne
          </h1>
          <p className="mx-auto max-w-3xl text-[15px] font-normal normal-case leading-relaxed text-[#d8fff7]">
            Liste des produits ARLA dont l&apos;étiquette autorise l&apos;application aérienne au Canada.
            Recherchez un produit, un titulaire ou un numéro d&apos;homologation.
          </p>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl px-4 py-8">
        <div
          className="mb-5 rounded-r-2xl border border-amber-300 border-l-4 border-l-amber-500 bg-amber-50 px-4 py-4 text-sm leading-relaxed text-amber-950"
          role="note"
        >
          <strong className="text-amber-900">⚠️ Important — toujours valider l&apos;étiquette officielle.</strong>{" "}
          Cette liste recense les produits dont l&apos;étiquette ARLA autorise l&apos;application aérienne. Elle est fournie à
          titre informatif seulement. L&apos;inscription d&apos;un produit ici ne garantit pas qu&apos;il puisse être appliqué par
          drone dans votre situation : des restrictions peuvent s&apos;appliquer selon le produit, la culture, la dose, les zones
          tampons, la région et la période. Avant toute application, consultez l&apos;étiquette officielle du produit et
          respectez intégralement ses directives d&apos;application aérienne, ainsi que la Lettre de non-objection de Santé
          Canada (PRO2026‑01), les exigences de Transports Canada et la certification provinciale d&apos;applicateur.
        </div>
        {loading && (
          <div className="rounded-xl border border-[#d0eeeb] bg-white px-6 py-10 text-center text-sm text-[#667085]">
            Chargement de la liste ARLA…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-10 text-center text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && payload && (
          <div className="space-y-5">
            <section className="rounded-2xl border border-[#e5e7eb] bg-white p-5 shadow-sm">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-sm text-[#667085]">
                    {filteredItems.length.toLocaleString("fr-CA")} produit
                    {filteredItems.length > 1 ? "s" : ""} affiché
                    {filteredItems.length > 1 ? "s" : ""}
                    {hasActiveFilters ? ` sur ${payload.total.toLocaleString("fr-CA")}` : ""}
                  </p>
                  <p className="mt-1 text-xs text-[#98a2b3]">
                    Source : {payload.source_description} · Mise à jour : {formatUpdatedAt(payload.updated_at)}
                  </p>
                </div>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-2 self-start rounded-lg border border-[#d0eeeb] bg-[#ecfdf5] px-3 py-2 text-sm font-medium text-[#0f766e] hover:bg-[#d1fae5]"
                  >
                    <X size={16} />
                    Réinitialiser les filtres
                  </button>
                )}
              </div>

              <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr_1fr]">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-[#344054]">Recherche</span>
                  <div className="relative">
                    <Search
                      size={18}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#98a2b3]"
                    />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Nom, n° homologation, titulaire…"
                      className="w-full rounded-xl border border-[#d0d5dd] bg-white py-2.5 pl-10 pr-3 text-sm outline-none ring-[#2ec4b6] focus:border-[#2ec4b6] focus:ring-2"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-[#344054]">Type de produit</span>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full rounded-xl border border-[#d0d5dd] bg-white px-3 py-2.5 text-sm outline-none ring-[#2ec4b6] focus:border-[#2ec4b6] focus:ring-2"
                  >
                    <option value="">Tous les types ({payload.total.toLocaleString("fr-CA")})</option>
                    {payload.product_types.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.label} ({type.count.toLocaleString("fr-CA")})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-[#344054]">Titulaire d&apos;homologation</span>
                  <select
                    value={registrantFilter}
                    onChange={(e) => setRegistrantFilter(e.target.value)}
                    className="w-full rounded-xl border border-[#d0d5dd] bg-white px-3 py-2.5 text-sm outline-none ring-[#2ec4b6] focus:border-[#2ec4b6] focus:ring-2"
                  >
                    <option value="">Tous les titulaires</option>
                    {payload.registrants.map((registrant) => (
                      <option key={registrant.name} value={registrant.name}>
                        {registrant.name} ({registrant.count.toLocaleString("fr-CA")})
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {payload.product_types.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setTypeFilter((current) => (current === type.id ? "" : type.id))}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
                      typeFilter === type.id
                        ? "border-[#0f766e] bg-[#0f766e] text-white"
                        : "border-[#d0eeeb] bg-[#f8fffd] text-[#0f766e] hover:bg-[#ecfdf5]"
                    )}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#064e3b] text-left text-white">
                      <th className="px-4 py-3 font-semibold">N° homologation</th>
                      <th className="px-4 py-3 font-semibold">Produit</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Titulaire</th>
                      <th className="px-4 py-3 font-semibold">Étiquette</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-[#667085]">
                          Aucun produit ne correspond à vos critères.
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((item) => (
                        <tr key={`${item.regnum}-${item.product_name}`} className="border-t border-[#eef2f6] odd:bg-[#fafafa]">
                          <td className="whitespace-nowrap px-4 py-3 font-medium text-[#111827]">{item.regnum}</td>
                          <td className="px-4 py-3 font-medium text-[#111827]">{item.product_name}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-[#475467]">{item.product_type_label}</td>
                          <td className="px-4 py-3 text-[#475467]">{item.registrant}</td>
                          <td className="px-4 py-3">
                            {item.label_url ? (
                              <a
                                href={item.label_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 font-medium text-[#0f766e] hover:underline"
                              >
                                Voir l&apos;étiquette
                                <ExternalLink size={14} />
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <p className="text-center text-xs leading-relaxed text-[#667085]">
              Cette liste est fournie à titre informatif. Consultez toujours l&apos;étiquette officielle du produit et
              respectez les exigences fédérales et provinciales applicables, y compris pour l&apos;application par drone.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
